import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import api, { formatApiError } from '../api';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot-password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((val || '').trim());

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    if (!forgotEmail.trim()) {
      setForgotError('Vennligst fyll inn e-postadressen din');
      return;
    }
    if (!isValidEmail(forgotEmail)) {
      setForgotError('Ugyldig e-postadresse — sjekk formatet (f.eks. navn@bedrift.no)');
      return;
    }
    setForgotLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: forgotEmail.trim().toLowerCase() });
      setForgotMessage(`Sjekk e-posten din 📧 — vi har sendt instruksjoner til ${forgotEmail.trim()}.`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setForgotError(formatApiError(detail) || 'Noe gikk galt. Prøv igjen senere.');
    } finally {
      setForgotLoading(false);
    }
  };

  const openForgot = () => {
    setForgotMode(true);
    setForgotEmail(email);
    setForgotError('');
    setForgotMessage('');
  };

  const closeForgot = () => {
    setForgotMode(false);
    setForgotError('');
    setForgotMessage('');
  };

  return (
    <div className="min-h-screen flex relative" data-testid="login-page">
      {/* Language switcher — top right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-navy-900 items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/10 to-transparent" />
        <div className="relative z-10 px-16 max-w-lg">
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo.png" alt="logo" className="w-10 h-10 rounded-lg object-cover" />
            <span className="font-heading text-2xl font-bold text-white tracking-tight">{t('product_name')}</span>
          </div>
          <h1
            className="font-heading text-4xl font-bold text-white leading-tight mb-6 tracking-tight"
            dangerouslySetInnerHTML={{ __html: t('login_headline') }}
          />
          <p className="text-slate-400 text-lg leading-relaxed">
            {t('login_subtext')}
          </p>
          <div className="mt-12 flex gap-8">
            <div>
              <div className="text-3xl font-heading font-bold text-white">24/7</div>
              <div className="text-sm text-slate-500 mt-1">{t('login_stat_always')}</div>
            </div>
            <div>
              <div className="text-3xl font-heading font-bold text-accent-green">98%</div>
              <div className="text-sm text-slate-500 mt-1">{t('login_stat_satisfied')}</div>
            </div>
            <div>
              <div className="text-3xl font-heading font-bold text-accent-blue">50k+</div>
              <div className="text-sm text-slate-500 mt-1">{t('login_stat_calls')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 bg-navy-900">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src="/logo.png" alt="logo" className="w-9 h-9 rounded-lg object-cover" />
            <span className="font-heading text-xl font-bold text-white tracking-tight">{t('product_name')}</span>
          </div>

          <h2 className="font-heading text-2xl font-bold text-white tracking-tight mb-2">{t('login_title')}</h2>
          <p className="text-slate-500 mb-8">{t('login_subtitle')}</p>

          {error && (
            <div className="bg-accent-red/10 border border-accent-red/20 text-accent-red px-4 py-3 rounded-lg mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">{t('login_email')}</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-colors"
                placeholder={t('login_email_placeholder')}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">{t('login_password')}</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-colors pr-12"
                  placeholder={t('login_password_placeholder')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-3.5 h-3.5 rounded border-navy-600 bg-navy-800 text-accent-blue focus:ring-accent-blue" />
                <span className="text-xs text-slate-500">{t('login_remember')}</span>
              </label>
              <button type="button" onClick={openForgot}
                className="text-xs text-accent-blue hover:text-accent-blue-hover transition-colors" data-testid="forgot-password-link">
                {t('login_forgot')}
              </button>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('login_loading') : t('login_button')}
            </button>
          </form>
        </div>
      </div>

      {/* Forgot password modal */}
      {forgotMode && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="forgot-password-modal"
          onClick={closeForgot}
        >
          <div
            className="w-full max-w-md bg-navy-800 border border-navy-600 rounded-xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeForgot}
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors mb-4"
              data-testid="forgot-password-back"
            >
              <ArrowLeft size={14} /> Tilbake
            </button>
            <h3 className="font-heading text-xl font-bold text-white tracking-tight mb-2">Glemt passord</h3>
            <p className="text-slate-400 text-sm mb-6">
              Skriv inn e-postadressen din, så sender vi deg en tilbakestillingslenke.
            </p>

            {forgotError && (
              <div className="bg-accent-red/10 border border-accent-red/20 text-accent-red px-4 py-3 rounded-lg mb-4 text-sm" data-testid="forgot-password-error">
                {forgotError}
              </div>
            )}
            {forgotMessage && (
              <div className="bg-accent-green/10 border border-accent-green/20 text-accent-green px-4 py-3 rounded-lg mb-4 text-sm" data-testid="forgot-password-success">
                {forgotMessage}
              </div>
            )}

            {!forgotMessage && (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">E-post</label>
                  <input
                    data-testid="forgot-password-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-colors"
                    placeholder="din@epost.no"
                    required
                    autoFocus
                  />
                </div>
                <button
                  data-testid="forgot-password-submit"
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {forgotLoading ? 'Sender...' : 'Send tilbakestillingslenke'}
                </button>
              </form>
            )}
            {forgotMessage && (
              <button
                type="button"
                onClick={closeForgot}
                className="w-full py-3 mt-2 bg-navy-700 hover:bg-navy-600 text-white font-semibold rounded-lg transition-colors"
              >
                Lukk
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
